import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import {
  segmentedTabsListVariants,
  segmentedTabsTriggerVariants,
  type SegmentedTabsVariant,
} from '@/components/ui/segmented-tabs-variants';

export type SegmentedTabItem<T extends string = string> = {
  value: T;
  label: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  hidden?: boolean;
  disabled?: boolean;
};

type SegmentedTabsVariantProps = VariantProps<typeof segmentedTabsListVariants>;

const SegmentedTabs = TabsPrimitive.Root;

const SegmentedTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & SegmentedTabsVariantProps
>(({ className, variant = 'pill', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(segmentedTabsListVariants({ variant }), className)}
    {...props}
  />
));
SegmentedTabsList.displayName = 'SegmentedTabsList';

const SegmentedTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & SegmentedTabsVariantProps
>(({ className, variant = 'pill', children, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(segmentedTabsTriggerVariants({ variant }), className)}
    {...props}
  >
    {children}
  </TabsPrimitive.Trigger>
));
SegmentedTabsTrigger.displayName = 'SegmentedTabsTrigger';

const SegmentedTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
SegmentedTabsContent.displayName = 'SegmentedTabsContent';

type SegmentedTabBarProps<T extends string> = Omit<
  React.ComponentPropsWithoutRef<typeof SegmentedTabs>,
  'children' | 'onValueChange'
> & {
  items: SegmentedTabItem<T>[];
  variant?: SegmentedTabsVariant;
  listClassName?: string;
  triggerClassName?: string;
  onValueChange?: (value: T) => void;
};

function SegmentedTabBar<T extends string>({
  items,
  variant = 'pill',
  listClassName,
  triggerClassName,
  onValueChange,
  ...tabsProps
}: SegmentedTabBarProps<T>) {
  const visibleItems = items.filter((item) => !item.hidden);

  return (
    <SegmentedTabs
      {...tabsProps}
      onValueChange={onValueChange ? (value) => onValueChange(value as T) : undefined}
    >
      <SegmentedTabsList variant={variant} className={listClassName}>
        {visibleItems.map((item) => {
          const Icon = item.icon;

          return (
            <SegmentedTabsTrigger
              key={item.value}
              value={item.value}
              variant={variant}
              disabled={item.disabled}
              className={triggerClassName}
            >
              {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
              {item.label}
            </SegmentedTabsTrigger>
          );
        })}
      </SegmentedTabsList>
    </SegmentedTabs>
  );
}

export {
  SegmentedTabs,
  SegmentedTabsList,
  SegmentedTabsTrigger,
  SegmentedTabsContent,
  SegmentedTabBar,
  type SegmentedTabsVariant,
};
